/**
 * SPEC-TrainingSimulator §7 — Senior/Admin view at /train/manage.
 *
 * Tabs: Training Set · Trainees · Questions · Lessons · Settings.
 *   - Training Set (§7.1): add box, counter strip (served set per phase, D21),
 *     table with sequence / eligible phases / readiness / In set / status /
 *     Edit key / Retire.
 *   - Questions (§7.3, D15): prompt, labels, options, default feedback,
 *     enabled, mandatory-by-phase. Admin writes; seniors read.
 *   - Lessons (§7.3a, D19): markdown + images + placement, with preview.
 *   - Settings (§5.1): Admin-only form.
 *   - Trainees (§7.4, Phase D): roster with the at-a-glance Phase 4 scores
 *     (first attempt beside latest), extensions, certification state; the
 *     per-attempt drill-down; and the three actions — blind retake (D8),
 *     certify (D17), and Promote to Agent (a link to Access Management; role
 *     changes are not this page's job).
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useRole } from "@/hooks/use-role";
import { Starling } from "@/lib/starling-brand";
import {
  useAddSimulatorItems,
  useCertifyTrainee,
  useDeleteSimulatorLesson,
  useOpenRetake,
  useSaveSimulatorLesson,
  useSimulatorItems,
  useSimulatorLessons,
  useSimulatorQuestions,
  useSimulatorSettings,
  useSimulatorTrainee,
  useSimulatorTrainees,
  useUpdateSimulatorItem,
  useUpdateSimulatorQuestion,
  useUpdateSimulatorSettings,
  type AddLineResult,
  type SimulatorItemRow,
  type SimulatorLesson,
  type SimulatorLessonImage,
  type RosterScore,
  type SimulatorQuestionOut,
  type TraineeDetail,
  type TraineeRosterRow,
  type TraineeRosterSummary,
} from "@/lib/simulator-queries";
import {
  LESSON_MAX_IMAGES,
  LESSON_MAX_IMAGE_BYTES,
  PHASE_LABELS,
  SIMULATOR_PHASES,
  ITEM_FILTER_DEFS,
  dataUriBytes,
  describeKeyFacets,
  itemMatchesFilters,
  retakeCheck,
  validateLessonPlacement,
  type LessonPlacement,
  type SimulatorSettings,
} from "@shared/simulator-helpers";
import { SimpleMarkdown } from "@/components/simulator/simple-markdown";
import { ReadinessBadges } from "@/pages/train-manage-item";

const T = {
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.10)",
  good: "#166534",
  goodBg: "#DCFCE7",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  amber: "#B45309",
  amberBg: "#FEF3C7",
  sub: "#F3F4F6",
  card: "#FFFFFF",
} as const;

const TAB_VALUES = ["training-set", "trainees", "questions", "lessons", "settings"] as const;
type TabValue = (typeof TAB_VALUES)[number];

/**
 * Questions an Admin may not disable or unpin from phases 3–4 (§7.3, D15).
 * q10 is the decision itself; q11 is how the bad reason is graded — turning
 * either off breaks grading silently rather than loudly.
 */
const LOCKED_QUESTIONS: string[] = ["q10_decision", "q11_bad_reason"];

const inputStyle = { padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: Starling.fontBody, background: "#fff" } as const;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ─── Training Set tab ────────────────────────────────────────────────────────

function CounterStrip({ counters, phaseSizes, items }: { counters: NonNullable<ReturnType<typeof useSimulatorItems>["data"]>["counters"]; phaseSizes: Record<number, number>; items: SimulatorItemRow[] }) {
  const byId = new Map(items.map((i) => [i.id, i]));
  return (
    <div data-testid="simulator-counter-strip" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
      {SIMULATOR_PHASES.map((p) => {
        const c = counters[p];
        const color = c.state === "short" ? { bg: T.badBg, fg: T.bad, border: "#FECACA" } : c.state === "over" ? { bg: T.amberBg, fg: T.amber, border: "#FDE68A" } : { bg: T.goodBg, fg: T.good, border: "#86EFAC" };
        const name = ["", "Learn", "Practice", "Normal QA", "Strict"][p];
        return (
          <div key={p} data-testid={`counter-phase-${p}`} data-state={c.state} style={{ background: color.bg, border: `1px solid ${color.border}`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: color.fg, textTransform: "uppercase", letterSpacing: "0.03em" }}>Phase {p} · {name}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color.fg, fontFamily: Starling.fontHead }}>{c.readyInSet} / {phaseSizes[p] ?? c.required} ready</div>
            <div style={{ fontSize: 11.5, color: color.fg }}>
              {c.state === "short" && "Not enough ready items — trainees can't start this phase."}
              {c.state === "ok" && "Exactly the served set."}
              {c.state === "over" && `${c.overflowIds.length} over — benched: ${c.overflowIds.map((id) => byId.get(id)?.recipientName ?? id.slice(0, 6)).join(", ")}`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AddBox() {
  const add = useAddSimulatorItems();
  const [text, setText] = useState("");
  const [results, setResults] = useState<AddLineResult[] | null>(null);
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const submit = async () => {
    if (!lines.length) return;
    const r = await add.mutateAsync(lines);
    setResults(r.results);
    if (r.added > 0) setText(r.results.filter((x) => x.status === "rejected").map((x) => x.line).join("\n"));
  };
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 14 }} data-testid="simulator-add-box">
      <div style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Add deliveries to the training set</div>
      <div style={{ fontSize: 12.5, color: T.txt2, marginBottom: 8 }}>
        One per line: an Onfleet dashboard link (taskId=…), an 8-character Onfleet short id, a QAM delivery link, or a bare delivery id. Only completed QATT_1 deliveries are accepted.
      </div>
      <textarea
        data-testid="simulator-add-lines"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder={"https://app.onfleet.com/dashboard#/manage?taskEdit=false&open=task&taskId=…\nd85c0018\nhttps://…/qa/delivery/…"}
        style={{ ...inputStyle, width: "100%", resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <Button size="sm" data-testid="simulator-add-button" disabled={!lines.length || add.isPending} onClick={submit}>
          {add.isPending ? "Adding…" : `Add to training${lines.length ? ` (${lines.length})` : ""}`}
        </Button>
        {add.isError && <span style={{ fontSize: 12.5, color: T.bad }}>{String((add.error as Error)?.message ?? "Failed")}</span>}
      </div>
      {results && (
        <ul data-testid="simulator-add-results" style={{ listStyle: "none", padding: 0, margin: "10px 0 0", display: "flex", flexDirection: "column", gap: 4 }}>
          {results.map((r, i) => (
            <li key={i} data-status={r.status} style={{ fontSize: 12.5, display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontWeight: 700, color: r.status === "added" ? T.good : T.bad, minWidth: 64 }}>{r.status === "added" ? "✓ added" : "✗ rejected"}</span>
              <span style={{ fontFamily: "ui-monospace, monospace", color: T.txt2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 480 }}>{r.line}</span>
              {r.status === "rejected" && <span style={{ color: T.bad }}>— {r.reason}</span>}
              {r.status === "added" && <Link href={`/train/manage/item/${r.itemId}`} style={{ color: T.purple }}>Edit key</Link>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SequenceInput({ item }: { item: SimulatorItemRow }) {
  const update = useUpdateSimulatorItem();
  const [v, setV] = useState(item.sequence == null ? "" : String(item.sequence));
  useEffect(() => { setV(item.sequence == null ? "" : String(item.sequence)); }, [item.sequence]);
  const commit = () => {
    const n = v.trim() === "" ? null : Number(v);
    if (n !== null && (!Number.isInteger(n) || n < 0)) { setV(item.sequence == null ? "" : String(item.sequence)); return; }
    if (n === item.sequence) return;
    update.mutate({ id: item.id, sequence: n });
  };
  return (
    <input
      type="number"
      min={0}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      data-testid={`item-sequence-${item.id}`}
      style={{ ...inputStyle, width: 64, padding: "4px 6px" }}
    />
  );
}

type ItemFilters = Record<string, string | undefined>;

/**
 * Six filters (Sarah, 2026-09-15): phase served in, decision, return
 * collection, past-photo conflict, what the POD is missing, expected drop
 * zone. They AND together; the Key column shows the same facts per row so a
 * Senior can read the matched keys side by side. Cmd/Ctrl-click any row link
 * to open that key in its own tab (wouter hands modifier clicks to the browser).
 */
function KeyFilterBar({ filters, onChange, matched, total }: { filters: ItemFilters; onChange: (next: ItemFilters) => void; matched: number; total: number }) {
  const active = ITEM_FILTER_DEFS.filter((d) => filters[d.id]);
  return (
    <div data-testid="items-key-filters" style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}`, background: T.sub, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: T.txt2 }}>Show</span>
      {ITEM_FILTER_DEFS.map((def) => {
        const v = filters[def.id] ?? "";
        return (
          <select key={def.id} data-testid={`items-key-filter-${def.id}`} value={v}
            onChange={(e) => onChange({ ...filters, [def.id]: e.target.value || undefined })}
            style={{ ...inputStyle, padding: "4px 8px", fontSize: 12.5, borderColor: v ? T.purple : T.border, color: v ? T.purple : T.text, fontWeight: v ? 600 : 400 }}>
            <option value="">{def.label} · any</option>
            {def.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      })}
      <span style={{ fontSize: 12.5, color: T.txt2, marginLeft: "auto" }}>
        {active.length === 0 ? "⌘-click a delivery to open its key in a new tab" : `${matched} of ${total} match`}
        {active.length > 0 && (
          <button type="button" data-testid="items-key-filters-clear" onClick={() => onChange({})}
            style={{ background: "none", border: "none", color: T.purple, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: Starling.fontBody, textDecoration: "underline", marginLeft: 8 }}>
            Clear
          </button>
        )}
      </span>
    </div>
  );
}

function ItemsTable({ items, counters }: { items: SimulatorItemRow[]; counters: Record<number, { servedIds: string[] }> }) {
  const update = useUpdateSimulatorItem();
  const [showRetired, setShowRetired] = useState(false);
  const [filters, setFilters] = useState<ItemFilters>({});
  const servedPhases = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const p of SIMULATOR_PHASES) for (const id of counters[p]?.servedIds ?? []) m.set(id, [...(m.get(id) ?? []), p]);
    return m;
  }, [counters]);
  const notRetired = items.filter((i) => showRetired || i.status !== "retired");
  const visible = notRetired.filter((i) => itemMatchesFilters(i.facets, { servedPhases: servedPhases.get(i.id) ?? [] }, filters));
  const togglePhase = (item: SimulatorItemRow, p: number) => {
    const next = item.eligiblePhases.includes(p) ? item.eligiblePhases.filter((x) => x !== p) : [...item.eligiblePhases, p].sort();
    update.mutate({ id: item.id, eligiblePhases: next });
  };
  const th = { textAlign: "left" as const, fontSize: 11.5, fontWeight: 600, color: T.txt2, padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textTransform: "uppercase" as const, letterSpacing: "0.03em", whiteSpace: "nowrap" as const };
  const td = { padding: "8px 10px", borderBottom: `1px solid ${T.border}`, fontSize: 13, verticalAlign: "middle" as const };
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px" }}>
        <div style={{ fontSize: 12.5, color: T.txt2 }}>{visible.length} item{visible.length === 1 ? "" : "s"}{!showRetired && items.some((i) => i.status === "retired") ? ` · ${items.filter((i) => i.status === "retired").length} retired hidden` : ""}</div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.txt2 }}>
          <Switch checked={showRetired} onCheckedChange={setShowRetired} data-testid="items-show-retired" /> Show retired
        </label>
      </div>
      <KeyFilterBar filters={filters} onChange={setFilters} matched={visible.length} total={notRetired.length} />
      <div style={{ overflowX: "auto" }}>
        <table data-testid="simulator-items-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Seq</th>
              <th style={th}>Recipient · address</th>
              <th style={th}>Key</th>
              <th style={th}>Added</th>
              <th style={th}>Eligible phases</th>
              <th style={th}>Ready</th>
              <th style={th}>In set</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} style={{ ...td, color: T.txt3, textAlign: "center", padding: 24 }}>{notRetired.length === 0 ? "No items yet — paste links above to add the first ones." : "No delivery matches these filters."}</td></tr>
            )}
            {visible.map((item) => {
              const retired = item.status === "retired";
              const statusStyle = item.status === "ready" ? { background: T.goodBg, color: T.good } : retired ? { background: T.sub, color: T.txt2 } : { background: T.amberBg, color: T.amber };
              return (
                <tr key={item.id} data-testid={`item-row-${item.id}`} data-status={item.status} style={{ opacity: retired ? 0.6 : 1 }}>
                  <td style={td}><SequenceInput item={item} /></td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}><Link href={`/train/manage/item/${item.id}`} data-testid={`item-open-${item.id}`} title="Open the key (⌘-click for a new tab)" style={{ color: T.text }}>{item.recipientName ?? "—"}</Link> {item.photoMissing && <span title="A POD photo is no longer on CloudFront" style={{ color: T.bad, fontSize: 12 }}>⚠ photo missing</span>}</div>
                    <div style={{ fontSize: 12, color: T.txt2 }}>{item.customer ?? "—"} · {item.address ?? "—"} · {item.photoCount} photo{item.photoCount === 1 ? "" : "s"} · {item.onfleetShortId ?? item.onfleetTaskId}</div>
                  </td>
                  <td data-testid={`item-key-${item.id}`} style={{ ...td, fontSize: 12, color: T.txt2, minWidth: 220, maxWidth: 360 }}>
                    {item.facets.decision && <span style={{ fontWeight: 700, color: item.facets.decision === "BAD" ? T.bad : T.good, marginRight: 6 }}>{item.facets.decision}</span>}
                    {describeKeyFacets({ ...item.facets, decision: null }) || <span style={{ color: T.txt3 }}>not keyed</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap", color: T.txt2 }}>{fmtDate(item.addedAt)}</td>
                  <td style={td}>
                    <div style={{ display: "inline-flex", gap: 4 }}>
                      {SIMULATOR_PHASES.map((p) => {
                        const on = item.eligiblePhases.includes(p);
                        return (
                          <button key={p} type="button" aria-pressed={on} data-testid={`item-phase-${item.id}-${p}`} disabled={retired}
                            onClick={() => togglePhase(item, p)}
                            style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${on ? T.purple : T.border}`, background: on ? T.purple : "#fff", color: on ? "#fff" : T.txt2, fontWeight: 700, fontSize: 11.5, cursor: retired ? "not-allowed" : "pointer" }}>
                            {p}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td style={td}><ReadinessBadges readiness={item.readiness} eligiblePhases={item.eligiblePhases} testIdPrefix={`item-readiness-${item.id}`} /></td>
                  <td style={td}>
                    <Switch checked={item.inSet} disabled={retired} onCheckedChange={(v) => update.mutate({ id: item.id, inSet: v })} data-testid={`item-inset-${item.id}`} />
                  </td>
                  <td style={td}><span data-testid={`item-status-${item.id}`} style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, ...statusStyle }}>{item.status}</span></td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <Link href={`/train/manage/item/${item.id}`} data-testid={`item-edit-${item.id}`} style={{ color: T.purple, fontWeight: 600, marginRight: 10 }}>Edit key</Link>
                    <button type="button" data-testid={`item-retire-${item.id}`} onClick={() => update.mutate({ id: item.id, status: retired ? "draft" : "retired" })}
                      style={{ background: "none", border: "none", color: T.txt2, cursor: "pointer", fontSize: 12.5, fontFamily: Starling.fontBody, textDecoration: "underline" }}>
                      {retired ? "Un-retire" : "Retire"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrainingSetTab() {
  const q = useSimulatorItems();
  if (q.isLoading) return <div style={{ padding: 12 }}>Loading training set…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 12, color: T.bad }}>Couldn't load the training set: {String((q.error as Error)?.message ?? "")}</div>;
  return (
    <div>
      <CounterStrip counters={q.data.counters} phaseSizes={q.data.phaseSizes} items={q.data.items} />
      <AddBox />
      <ItemsTable items={q.data.items} counters={q.data.counters} />
    </div>
  );
}

// ─── Questions tab ───────────────────────────────────────────────────────────

function KeyValueEditor({ label, value, onChange, disabled }: { label: string; value: Record<string, string> | undefined; onChange: (v: Record<string, string>) => void; disabled: boolean }) {
  const entries = Object.entries(value ?? {});
  if (entries.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: T.txt2, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "4px 8px", alignItems: "center" }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <code style={{ fontSize: 11.5, color: T.txt2 }}>{k}</code>
            <input value={v} disabled={disabled} onChange={(e) => onChange({ ...(value ?? {}), [k]: e.target.value })} style={{ ...inputStyle, padding: "4px 8px", fontSize: 12.5 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionCard({ q, canEdit }: { q: SimulatorQuestionOut; canEdit: boolean }) {
  const update = useUpdateSimulatorQuestion();
  const { toast } = useToast();
  const [draft, setDraft] = useState(q);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setDraft(q); setDirty(false); }, [q.updatedAt, q.id]);
  const patch = (p: Partial<SimulatorQuestionOut>) => { setDraft({ ...draft, ...p }); setDirty(true); };
  const patchText = (p: Partial<SimulatorQuestionOut["text"]>) => patch({ text: { ...draft.text, ...p } });
  // Both decision questions are locked mandatory in phases 3–4: q10 is the footer
  // click itself, and without q11 the bad reason is silently never graded there
  // (§12 Q1, AUDIT-Final M2).
  const lockedPhases = LOCKED_QUESTIONS.includes(q.id) ? [3, 4] : [];
  const save = async () => {
    try {
      await update.mutateAsync({ id: q.id, text: draft.text, enabled: draft.enabled, mandatoryPhases: draft.mandatoryPhases });
      toast({ title: `Saved ${q.id}` });
    } catch (e: any) {
      toast({ title: "Couldn't save question", description: String(e?.message ?? e), variant: "destructive" });
    }
  };
  return (
    <div data-testid={`question-card-${q.id}`} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontFamily: Starling.fontHead, fontWeight: 600, fontSize: 14 }}><span style={{ color: T.purple }}>Q{q.sort}</span> <code style={{ fontSize: 12, color: T.txt2 }}>{q.id}</code></div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
          <Switch checked={draft.enabled} disabled={!canEdit || LOCKED_QUESTIONS.includes(q.id)} onCheckedChange={(v) => patch({ enabled: v })} data-testid={`question-enabled-${q.id}`} /> Enabled{LOCKED_QUESTIONS.includes(q.id) ? " 🔒" : ""}
        </label>
      </div>
      {/* Fixed descriptors (§7.3 — not editable). */}
      <div style={{ fontSize: 12, color: T.txt2, background: T.sub, borderRadius: 6, padding: "6px 10px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px" }}>
        <span>Type</span><span style={{ color: T.text }}>{q.type}</span>
        <span>Required action</span><span style={{ color: T.text }}>{q.requiredAction}</span>
        <span>Branches</span><span style={{ color: T.text }}>{q.branches}</span>
      </div>
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: T.txt2, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 }}>Prompt</div>
        <input data-testid={`question-prompt-${q.id}`} value={draft.text.prompt} disabled={!canEdit} onChange={(e) => patchText({ prompt: e.target.value })} style={{ ...inputStyle, width: "100%" }} />
      </div>
      <KeyValueEditor label="Option labels" value={draft.text.options} disabled={!canEdit} onChange={(options) => patchText({ options })} />
      <KeyValueEditor label="Field labels" value={draft.text.labels} disabled={!canEdit} onChange={(labels) => patchText({ labels })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: T.good, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 }}>Default feedback · correct</div>
          <textarea rows={2} disabled={!canEdit} value={draft.text.feedbackDefault.correct} onChange={(e) => patchText({ feedbackDefault: { ...draft.text.feedbackDefault, correct: e.target.value } })} style={{ ...inputStyle, width: "100%", resize: "vertical" }} />
        </div>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: T.bad, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 4 }}>Default feedback · wrong</div>
          <textarea rows={2} disabled={!canEdit} value={draft.text.feedbackDefault.wrong} onChange={(e) => patchText({ feedbackDefault: { ...draft.text.feedbackDefault, wrong: e.target.value } })} style={{ ...inputStyle, width: "100%", resize: "vertical" }} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
          <span style={{ color: T.txt2 }}>Mandatory in phase:</span>
          {SIMULATOR_PHASES.map((p) => {
            const on = draft.mandatoryPhases.includes(p);
            const locked = lockedPhases.includes(p);
            return (
              <label key={p} style={{ display: "inline-flex", alignItems: "center", gap: 3, opacity: locked ? 0.7 : 1 }} title={locked ? "Locked mandatory (§7.3)" : undefined}>
                <input type="checkbox" checked={on} disabled={!canEdit || locked} data-testid={`question-mandatory-${q.id}-${p}`}
                  onChange={(e) => patch({ mandatoryPhases: e.target.checked ? [...draft.mandatoryPhases, p].sort() : draft.mandatoryPhases.filter((x) => x !== p) })} />
                {p}{locked ? " 🔒" : ""}
              </label>
            );
          })}
        </div>
        {canEdit ? (
          <Button size="sm" data-testid={`question-save-${q.id}`} disabled={!dirty || update.isPending} onClick={save}>{update.isPending ? "Saving…" : "Save"}</Button>
        ) : (
          <span style={{ fontSize: 12, color: T.txt3 }}>Admin edits question text</span>
        )}
      </div>
    </div>
  );
}

function QuestionsTab({ canEdit }: { canEdit: boolean }) {
  const q = useSimulatorQuestions();
  if (q.isLoading) return <div style={{ padding: 12 }}>Loading questions…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 12, color: T.bad }}>Couldn't load questions.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 12.5, color: T.txt2 }}>Question ids, answer types and branch structure are code. Wording, default feedback, enabled and mandatory-by-phase are yours (D15). Changes apply to future renders immediately; historic answers keep their ids.</div>
      {q.data.questions.map((question) => <QuestionCard key={question.id} q={question} canEdit={canEdit} />)}
    </div>
  );
}

// ─── Lessons tab ─────────────────────────────────────────────────────────────

const EMPTY_LESSON = { title: "", bodyMd: "", images: [] as SimulatorLessonImage[], placement: { phase: 0, position: "start" } as LessonPlacement, sort: 0, enabled: true };

function describePlacement(p: LessonPlacement): string {
  const pos = p.position === "start" ? "start" : p.position === "before_item" ? `before item ${(p.itemIndex ?? 0) + 1}` : "on first Bad";
  return `Phase ${p.phase} · ${pos}`;
}

function LessonForm({ initial, onDone }: { initial: (typeof EMPTY_LESSON & { id?: string }); onDone: () => void }) {
  const save = useSaveSimulatorLesson();
  const { toast } = useToast();
  const [d, setD] = useState(initial);
  const [imgUrl, setImgUrl] = useState("");
  const placementError = validateLessonPlacement(d.placement);
  const addImage = (img: SimulatorLessonImage) => {
    if (d.images.length >= LESSON_MAX_IMAGES) { toast({ title: `At most ${LESSON_MAX_IMAGES} images per lesson`, variant: "destructive" }); return; }
    if (dataUriBytes(img.src) > LESSON_MAX_IMAGE_BYTES) { toast({ title: "Image too large", description: `Keep it under ${LESSON_MAX_IMAGE_BYTES / 1024} KB.`, variant: "destructive" }); return; }
    setD({ ...d, images: [...d.images, img] });
  };
  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > LESSON_MAX_IMAGE_BYTES) { toast({ title: "Image too large", description: `Keep it under ${LESSON_MAX_IMAGE_BYTES / 1024} KB.`, variant: "destructive" }); return; }
    const reader = new FileReader();
    reader.onload = () => addImage({ src: String(reader.result), alt: file.name });
    reader.readAsDataURL(file);
  };
  const submit = async () => {
    try {
      await save.mutateAsync({ id: d.id, title: d.title, bodyMd: d.bodyMd, images: d.images, placement: d.placement, sort: d.sort, enabled: d.enabled });
      toast({ title: d.id ? "Lesson updated" : "Lesson created" });
      onDone();
    } catch (e: any) {
      toast({ title: "Couldn't save lesson", description: String(e?.message ?? e), variant: "destructive" });
    }
  };
  const label = { fontSize: 11.5, fontWeight: 600, color: T.txt2, textTransform: "uppercase" as const, letterSpacing: "0.03em", marginBottom: 4 };
  return (
    <div data-testid="lesson-form" style={{ background: T.card, border: `1px solid ${T.purple}`, borderRadius: 8, padding: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div><div style={label}>Title</div><input data-testid="lesson-title" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} style={{ ...inputStyle, width: "100%" }} /></div>
        <div><div style={label}>Body (markdown: # headings, lists, **bold**, links)</div>
          <textarea data-testid="lesson-body" rows={10} value={d.bodyMd} onChange={(e) => setD({ ...d, bodyMd: e.target.value })} style={{ ...inputStyle, width: "100%", resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 12.5 }} /></div>
        <div>
          <div style={label}>Placement</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select data-testid="lesson-phase" value={d.placement.phase} onChange={(e) => setD({ ...d, placement: { ...d.placement, phase: Number(e.target.value) as LessonPlacement["phase"] } })} style={inputStyle}>
              {[0, 1, 2, 3, 4].map((p) => <option key={p} value={p}>Phase {p}{p === 0 ? " (orientation)" : ""}</option>)}
            </select>
            <select data-testid="lesson-position" value={d.placement.position} onChange={(e) => setD({ ...d, placement: { ...d.placement, position: e.target.value as LessonPlacement["position"] } })} style={inputStyle}>
              <option value="start">start of phase</option>
              <option value="before_item">before item n</option>
              <option value="on_first_bad">on first Bad (phases 3–4)</option>
            </select>
            {d.placement.position === "before_item" && (
              <label style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 4 }}>item #
                <input type="number" min={1} value={(d.placement.itemIndex ?? 0) + 1} onChange={(e) => setD({ ...d, placement: { ...d.placement, itemIndex: Math.max(0, Number(e.target.value) - 1) } })} style={{ ...inputStyle, width: 70 }} />
              </label>
            )}
            <label style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 4 }}>sort
              <input type="number" value={d.sort} onChange={(e) => setD({ ...d, sort: Number(e.target.value) || 0 })} style={{ ...inputStyle, width: 70 }} />
            </label>
            <label style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6 }}><Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} /> Enabled</label>
          </div>
          {placementError && <div style={{ fontSize: 12, color: T.bad, marginTop: 4 }}>{placementError}</div>}
        </div>
        <div>
          <div style={label}>Images ({d.images.length}/{LESSON_MAX_IMAGES}, ≤ {LESSON_MAX_IMAGE_BYTES / 1024} KB each)</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input placeholder="https://… image URL" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
            <Button size="sm" variant="outline" disabled={!/^https:\/\//i.test(imgUrl)} onClick={() => { addImage({ src: imgUrl }); setImgUrl(""); }}>Add URL</Button>
            <label style={{ fontSize: 12.5, color: T.purple, cursor: "pointer", fontWeight: 600 }}>
              Upload… <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { onFile(e.target.files?.[0]); e.currentTarget.value = ""; }} />
            </label>
          </div>
          {d.images.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              {d.images.map((img, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ width: 44, height: 44, borderRadius: 6, background: `#E5E7EB url(${img.src}) center/cover`, flexShrink: 0 }} />
                  <input placeholder="Caption" value={img.caption ?? ""} onChange={(e) => setD({ ...d, images: d.images.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)) })} style={{ ...inputStyle, flex: 1 }} />
                  <button type="button" onClick={() => setD({ ...d, images: d.images.filter((_, j) => j !== i) })} style={{ background: "none", border: "none", color: T.bad, cursor: "pointer" }}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="sm" data-testid="lesson-save" disabled={!d.title.trim() || !!placementError || save.isPending} onClick={submit}>{save.isPending ? "Saving…" : d.id ? "Save lesson" : "Create lesson"}</Button>
          <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
        </div>
      </div>
      <div>
        <div style={label}>Preview (what the trainee sees)</div>
        <div data-testid="lesson-preview" style={{ background: "#F7F8FA", border: `1px solid ${T.border}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontFamily: Starling.fontHead, fontSize: 20, fontWeight: 600, marginBottom: 6 }}>{d.title || "Untitled lesson"}</div>
          <SimpleMarkdown source={d.bodyMd} />
          {d.images.map((img, i) => (
            <figure key={i} style={{ margin: "12px 0 0" }}>
              <img src={img.src} alt={img.alt ?? ""} style={{ maxWidth: "100%", borderRadius: 6, border: `1px solid ${T.border}` }} />
              {img.caption && <figcaption style={{ fontSize: 12, color: T.txt2, marginTop: 4 }}>{img.caption}</figcaption>}
            </figure>
          ))}
          <div style={{ marginTop: 14 }}><Button size="sm" disabled>Continue</Button></div>
        </div>
      </div>
    </div>
  );
}

function LessonsTab() {
  const q = useSimulatorLessons();
  const del = useDeleteSimulatorLesson();
  const [editing, setEditing] = useState<(typeof EMPTY_LESSON & { id?: string }) | null>(null);
  if (q.isLoading) return <div style={{ padding: 12 }}>Loading lessons…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 12, color: T.bad }}>Couldn't load lessons.</div>;
  const lessons = q.data.lessons;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 12.5, color: T.txt2 }}>Admin-authored transition pages (D19). Place one at the start of a phase, before an item, or on the first Bad in phases 3–4. Phase 0 lessons run before Phase 1 unlocks.</div>
        {!editing && <Button size="sm" data-testid="lesson-new" onClick={() => setEditing({ ...EMPTY_LESSON, sort: lessons.length })}>New lesson</Button>}
      </div>
      {editing && <LessonForm key={editing.id ?? "new"} initial={editing} onDone={() => setEditing(null)} />}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8 }}>
        {lessons.length === 0 && <div style={{ padding: 20, color: T.txt3, fontSize: 13, textAlign: "center" }}>No lessons yet.</div>}
        {lessons.map((l: SimulatorLesson) => (
          <div key={l.id} data-testid={`lesson-row-${l.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${T.border}`, opacity: l.enabled ? 1 : 0.6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{l.title} {!l.enabled && <span style={{ fontSize: 11, color: T.txt3 }}>(disabled)</span>}</div>
              <div style={{ fontSize: 12, color: T.txt2 }}>{describePlacement(l.placement)} · sort {l.sort} · {l.images.length} image{l.images.length === 1 ? "" : "s"}</div>
            </div>
            <Button size="sm" variant="outline" data-testid={`lesson-edit-${l.id}`} onClick={() => setEditing({ id: l.id, title: l.title, bodyMd: l.bodyMd, images: l.images, placement: l.placement, sort: l.sort, enabled: l.enabled })}>Edit</Button>
            <Button size="sm" variant="ghost" data-testid={`lesson-delete-${l.id}`} onClick={() => { if (window.confirm(`Delete lesson “${l.title}”?`)) del.mutate(l.id); }}>Delete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Trainees tab (Phase B: read-only roster + per-question drill-down; retakes / certify land in Phase D) ──

/**
 * Trainees tab (§7.4, D7 / D8 / D17) — Phase D.
 *
 * The roster reads only what is already stored: the per-attempt `summary` (§9,
 * recomputed on finish and on every regrade, D22) and the certification rows.
 * Nothing is recalculated here, so a regrade flows straight through.
 *
 * Deliberately absent: any automatic pass/fail verdict. `passRecommended` is
 * still computed and stored (D23) but the server strips it from these payloads
 * — Sarah's call on 2026-09-10 is that the Senior reads the performance and
 * signs the result themselves (§12 Q7 stays open).
 */
function scorePair(s: RosterScore | null): string {
  return s ? `${s.accuracyPct}% / ${s.withinTargetPct}%` : "—";
}

/**
 * The false-clear / false-flag split §9 and §12 Q7 promise on the roster row.
 * `accuracyPct` counts both alike, and they are not alike: a keyed-Bad called
 * Good is waved through to the customer. Shown inline when there is a false
 * clear, in the hover otherwise, so a clean run stays quiet.
 */
function ScoreCell({ score, testid }: { score: RosterScore | null; testid: string }) {
  if (!score) return <span data-testid={testid}>—</span>;
  const title = `${score.accuracyPct}% decisions matched the key · ${score.withinTargetPct}% within pace · ${score.falseClears} keyed-Bad called Good · ${score.falseFlags} keyed-Good called Bad · ${score.noDecision} no decision · ${score.items} deliveries`;
  return (
    <span data-testid={testid} title={title}>
      {scorePair(score)}
      {score.falseClears > 0 && (
        <div data-testid={`${testid}-false-clears`} style={{ fontSize: 11, color: T.bad, fontWeight: 600 }}>
          {score.falseClears} waved through
        </div>
      )}
    </span>
  );
}

function CertPill({ cert, stale }: { cert: TraineeRosterSummary["certification"]; stale: boolean | null }) {
  if (!cert) return <span data-testid="cert-pill" data-result="none" style={{ fontSize: 11.5, color: T.txt3 }}>Not certified</span>;
  const pass = cert.result === "pass";
  return (
    <>
      <span
        data-testid="cert-pill"
        data-result={cert.result}
        data-stale={stale ? "true" : "false"}
        title={`${cert.signedByName ?? "Signed"}${cert.signedAt ? ` · ${fmtDate(cert.signedAt)}` : ""}${cert.attemptNo ? ` · attempt ${cert.attemptNo}` : ""}${cert.note ? `\n${cert.note}` : ""}`}
        style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: pass ? T.good : T.bad, background: pass ? T.goodBg : T.badBg }}
      >
        {pass ? "Certified" : "Failed"}
      </span>
      {/* The trainee has run the assessment again since this was signed (audit M2) —
          without this the roster reads "Certified" beside a worse, unjudged run. */}
      {stale && (
        <div data-testid="cert-stale" style={{ fontSize: 11, color: T.amber, fontWeight: 600, marginTop: 2 }}>
          attempt {cert.attemptNo} · a later run is unjudged
        </div>
      )}
    </>
  );
}

function TraineesTab() {
  const { isAdmin } = useRole();
  const q = useSimulatorTrainees();
  const [open, setOpen] = useState<string | null>(null);
  if (q.isLoading) return <div style={{ padding: 12 }}>Loading trainees…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 12, color: T.bad }}>Couldn't load trainees.</div>;
  const rows = q.data.trainees;
  const th: React.CSSProperties = { padding: "7px 10px", borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: .4, color: T.txt2, textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "9px 10px", fontSize: 12.5, verticalAlign: "middle" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 12.5, color: T.txt2 }}>
        Everyone with the <b>Trainee</b> role (Access Management) plus anyone who has attempted a phase. Scores are Senior-only (D7) —
        a trainee never sees any of this, and a retake tells them nothing about the attempt it repeats (D8).
        There is no automatic pass mark: read the numbers, then sign the result yourself.
      </div>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflowX: "auto" }} data-testid="trainees-roster">
        {rows.length === 0 && <div style={{ padding: 20, color: T.txt3, fontSize: 13, textAlign: "center" }}>No trainees yet. Create one in Access Management with the Trainee role.</div>}
        {rows.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Trainee</th>
                <th style={th}>Now</th>
                <th style={th} title="Phase 4, attempt 1 — accuracy / within pace target">P4 first</th>
                <th style={th} title="Phase 4, latest complete attempt — the one a certification judges">P4 latest</th>
                <th style={th} title="&ldquo;+ Time&rdquo; presses across every attempt">Extensions</th>
                <th style={th}>Certification</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t: TraineeRosterRow) => {
                const r = t.roster;
                const isOpen = open === t.userId;
                return (
                  <Fragment key={t.userId}>
                    <tr data-testid={`trainee-row-${t.userId}`} style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: T.txt3 }}>{t.email}{t.role !== "trainee" ? ` · ${t.role}` : ""}{!t.isActive ? " · inactive" : ""}</div>
                      </td>
                      <td style={td} data-testid={`trainee-now-${t.userId}`}>
                        {r.currentPhase === null ? <span style={{ color: T.txt3 }}>Not started</span> : (
                          <>
                            <div>Phase {r.currentPhase} · {PHASE_LABELS[r.currentPhase]}</div>
                            <div style={{ fontSize: 11, color: T.txt2 }}>
                              attempt {r.currentAttemptNo}
                              {r.currentStatus === "complete" ? " · complete" : ` · ${r.currentProgress.done}${r.currentProgress.total ? ` of ${r.currentProgress.total}` : ""} decided`}
                            </div>
                          </>
                        )}
                        {r.retakesOpen.length > 0 && (
                          <div data-testid={`trainee-retakes-open-${t.userId}`} style={{ fontSize: 11, color: T.amber, fontWeight: 600 }}>
                            assessment open · phase {r.retakesOpen.join(" + ")}
                          </div>
                        )}
                      </td>
                      <td style={td}><ScoreCell score={r.phase4First} testid={`trainee-p4-first-${t.userId}`} /></td>
                      <td style={td}>
                        <ScoreCell score={r.phase4Latest} testid={`trainee-p4-latest-${t.userId}`} />
                        {r.phase4Latest && r.phase4First && r.phase4Latest.attemptNo !== r.phase4First.attemptNo && (
                          <span style={{ fontSize: 11, color: T.txt3 }}> · attempt {r.phase4Latest.attemptNo}</span>
                        )}
                      </td>
                      <td style={td} data-testid={`trainee-extensions-${t.userId}`}>
                        {r.extensions.count === 0 ? <span style={{ color: T.txt3 }}>none</span> : `${r.extensions.count} · ${r.extensions.totalSeconds}s`}
                        {r.timeouts > 0 && <span style={{ color: T.amber }}> · {r.timeouts} timed out</span>}
                      </td>
                      <td style={td}><CertPill cert={r.certification} stale={r.certificationStale} /></td>
                      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                        {isAdmin && (
                          <Link href="/admin/users" data-testid={`trainee-promote-${t.userId}`} title="Role changes happen in Access Management" style={{ color: T.purple, fontWeight: 600, fontSize: 12, marginRight: 10 }}>
                            Promote
                          </Link>
                        )}
                        <Button size="sm" variant="outline" data-testid={`trainee-open-${t.userId}`} onClick={() => setOpen(isOpen ? null : t.userId)}>{isOpen ? "Hide" : "Drill down"}</Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, background: "#FAFAFB", borderTop: `1px solid ${T.border}` }}>
                          <TraineeDrillDown userId={t.userId} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Retake + certify panel (§7.4 actions, §10 rows).
 *
 * Retake is deliberately NOT a certification result: opening another run is
 * declining to sign, not signing something, so it writes no certification row
 * (Sarah, 2026-09-10 — and `simulator_certifications.result` only accepts
 * pass/fail, so a third value would need DDL against the live DB).
 */
function TraineeActions({ userId, role, roster, attempts }: { userId: string; role: string; roster: TraineeRosterSummary; attempts: TraineeDetail["attempts"] }) {
  const { toast } = useToast();
  const retake = useOpenRetake(userId);
  const certify = useCertifyTrainee(userId);
  const [note, setNote] = useState("");

  // One rule, shared with the server, so the button and the route never disagree.
  const check = useMemo(
    () => retakeCheck(attempts.map((a) => ({ id: a.id, phase: a.phase, attemptNo: a.attemptNo, status: a.status }))),
    [attempts],
  );
  // A promoted agent keeps their history on this roster, but guard T refuses them
  // every runner route — an assessment opened for one could never be worked, and
  // would block their certification for good (AUDIT-Final M1).
  const wrongRole = role !== "trainee";
  const canReopen = check.ok && !wrongRole;
  const reopenReason = wrongRole
    ? `No longer a trainee (role: ${role}) — an assessment opened now could never be worked. Move them back to Trainee in Access Management first.`
    : check.reason;

  const submitRetake = async () => {
    if (!window.confirm(`Reopen the assessment for this trainee — Phases 3 and 4 together, as attempt ${check.nextAttemptNo}?\n\nThey will see Phase 3 startable again and nothing about how the last one went.`)) return;
    const res = await retake.mutateAsync().catch((e) => {
      toast({ title: "Couldn't reopen the assessment", description: String(e?.message ?? e).replace(/^\d{3}:\s*/, ""), variant: "destructive" });
      return null;
    });
    if (!res) return;
    toast({
      title: `Assessment reopened — attempt ${res.attemptNo}`,
      description: "Phases 3 and 4 are open together. The trainee sees Phase 3 startable again, and nothing about the attempt it repeats.",
    });
  };

  const submitCertify = async (result: "pass" | "fail") => {
    const judged = roster.phase4Latest;
    if (!judged) return;
    if (!window.confirm(`Sign this trainee as ${result.toUpperCase()} on Phase 4 attempt ${judged.attemptNo} (accuracy ${judged.accuracyPct}%, pace ${judged.withinTargetPct}%, ${judged.falseClears} false clear${judged.falseClears === 1 ? "" : "s"})?`)) return;
    const res = await certify
      .mutateAsync({ result, note: note.trim() || undefined, attemptId: judged.attemptId })
      .catch((e) => {
        toast({ title: "Certification failed", description: String(e?.message ?? e).replace(/^\d{3}:\s*/, ""), variant: "destructive" });
        return null;
      });
    if (!res) return;
    toast({ title: `Signed ${result}`, description: `Phase 4 attempt ${res.certification.attemptNo}.` });
    setNote("");
  };

  const box: React.CSSProperties = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 300 };
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }} data-testid={`trainee-actions-${userId}`}>
      <div style={box} data-testid="retake-panel">
        <div style={{ fontWeight: 700, fontSize: 12.5 }}>Reopen the assessment</div>
        <div style={{ fontSize: 11.5, color: T.txt2 }}>
          Phases 3 and 4 together, as one attempt — they are scored as a unit, so they are reopened as a unit.
          Each phase serves its deliveries again in the same order. The trainee is told nothing: Phase 3 simply becomes
          startable again, and Phase 4 waits behind it (D8).
        </div>
        <div style={{ fontSize: 11.5, color: T.txt2 }}>
          Phases 1 and 2 are never reopened — they show the answers as the trainee works, so a second run could not be blind.
          That work stays available to review.
        </div>
        {!canReopen
          ? <div data-testid="retake-blocked" style={{ fontSize: 11.5, color: T.amber, background: T.amberBg, borderRadius: 6, padding: "6px 9px" }}>{reopenReason}</div>
          : <div style={{ fontSize: 11.5, color: T.txt3 }}>Opens Phase 3 and Phase 4 as attempt {check.nextAttemptNo}.</div>}
        <div>
          <Button size="sm" data-testid="retake-submit" disabled={!canReopen || retake.isPending} onClick={submitRetake}>
            {retake.isPending ? "Reopening…" : "Reopen assessment (Phases 3 + 4)"}
          </Button>
        </div>
      </div>

      <div style={box} data-testid="certify-panel">
        <div style={{ fontWeight: 700, fontSize: 12.5 }}>Certify</div>
        {!roster.certifiable ? (
          <div style={{ fontSize: 11.5, color: T.txt2 }} data-testid="certify-blocked">
            {roster.retakesOpen.length
              ? `The assessment is open (Phase ${roster.retakesOpen.join(" and ")}) — reopening it is declining to sign. Wait for it to finish.`
              : "Needs a completed Phase 4 attempt before it can be signed."}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11.5, color: T.txt2 }}>
              Judges Phase 4 <b>attempt {roster.phase4Latest?.attemptNo}</b> — accuracy {roster.phase4Latest?.accuracyPct}%, within target {roster.phase4Latest?.withinTargetPct}%
              {roster.phase4Latest?.badReasonPct != null ? `, bad reason ${roster.phase4Latest.badReasonPct}%` : ""}
              {roster.phase4Latest?.timeouts ? `, ${roster.phase4Latest.timeouts} timed out` : ""}.
            </div>
            {roster.phase4Latest && (
              <div data-testid="certify-confusion" style={{ fontSize: 11.5, color: roster.phase4Latest.falseClears > 0 ? T.bad : T.txt2 }}>
                <b>{roster.phase4Latest.falseClears}</b> keyed-Bad called Good{roster.phase4Latest.falseClears > 0 ? " — waved through" : ""} ·{" "}
                <b>{roster.phase4Latest.falseFlags}</b> keyed-Good called Bad · <b>{roster.phase4Latest.noDecision}</b> no decision.
                <div style={{ color: T.txt3 }}>Accuracy counts these alike. This is your call, not the system's.</div>
              </div>
            )}
            {roster.certificationStale && (
              <div data-testid="certify-stale" style={{ fontSize: 11.5, color: T.amber, background: T.amberBg, borderRadius: 6, padding: "6px 9px" }}>
                The signature on the roster judged attempt {roster.certification?.attemptNo} — attempt {roster.phase4Latest?.attemptNo} has not been judged.
              </div>
            )}
            <textarea
              data-testid="certify-note"
              rows={2}
              placeholder="Note (optional) — kept with the signature, never shown to the trainee"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ ...inputStyle, width: "100%", resize: "vertical", fontSize: 12.5 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="sm" data-testid="certify-pass" disabled={certify.isPending} onClick={() => submitCertify("pass")}>Sign pass</Button>
              <Button size="sm" variant="outline" data-testid="certify-fail" disabled={certify.isPending} onClick={() => submitCertify("fail")}>Sign fail</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TraineeDrillDown({ userId }: { userId: string }) {
  const q = useSimulatorTrainee(userId);
  if (q.isLoading) return <div style={{ padding: "8px 12px 14px", fontSize: 12.5 }}>Loading…</div>;
  if (q.isError || !q.data) return <div style={{ padding: "8px 12px 14px", fontSize: 12.5, color: T.bad }}>Couldn't load this trainee.</div>;
  const cell: React.CSSProperties = { padding: "4px 8px", borderBottom: `1px solid ${T.border}`, fontSize: 12, verticalAlign: "top" };
  const mark = (s: string | undefined) => (s === "correct" ? <span style={{ color: T.good, fontWeight: 700 }}>✓</span> : s === "wrong" ? <span style={{ color: T.bad, fontWeight: 700 }}>✗</span> : s === "no_answer" ? <span style={{ color: T.amber, fontWeight: 700 }}>–</span> : <span style={{ color: T.txt3 }}>·</span>);
  return (
    <div data-testid={`trainee-drilldown-${userId}`} style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 12 }}>
      <TraineeActions userId={userId} role={q.data.user.role} roster={q.data.roster} attempts={q.data.attempts} />
      {q.data.certifications.length > 0 && (
        <div data-testid="certification-history" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
          <b style={{ fontSize: 12.5 }}>Certification history</b>
          {q.data.certifications.map((c) => {
            const a = q.data!.attempts.find((x) => x.id === c.attemptId);
            return (
              <div key={c.id} data-testid={`certification-${c.id}`} style={{ marginTop: 4 }}>
                <span style={{ fontWeight: 700, color: c.result === "pass" ? T.good : T.bad }}>{c.result}</span>
                <span style={{ color: T.txt2 }}> · Phase 4 attempt {a?.attemptNo ?? "?"} · signed by {c.signedByName ?? "—"} · {fmtDate(c.signedAt)}</span>
                {c.note && <span style={{ color: T.txt2 }}> · “{c.note}”</span>}
              </div>
            );
          })}
        </div>
      )}
      {q.data.attempts.length === 0 && <div style={{ fontSize: 12.5, color: T.txt3 }}>No attempts yet.</div>}
      {q.data.attempts.map((a) => {
        // JSONB does not keep key order — sort the columns Q1…Q11.
        const qNo = (id: string) => Number(id.match(/^q(\d+)/)?.[1] ?? 99);
        const qids = Array.from(new Set(a.answers.flatMap((r) => Object.keys(r.grades ?? {})))).sort((x, y) => qNo(x) - qNo(y));
        const s = a.summary;
        return (
          <div key={a.id} data-testid={`trainee-attempt-${a.id}`} style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", background: T.card }}>
            <div style={{ padding: "8px 10px", background: T.sub, fontSize: 12.5, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <b>Phase {a.phase} · {PHASE_LABELS[a.phase]} · attempt {a.attemptNo}</b>
              <span style={{ color: T.txt2 }}>{a.status} · {a.decidedCount} decided · started {a.startedAt ? new Date(a.startedAt).toLocaleString() : "—"}{a.finishedAt ? ` · finished ${new Date(a.finishedAt).toLocaleString()}` : ""}</span>
              {s && (
                <span data-testid="attempt-summary" style={{ color: T.purple, fontWeight: 600 }}>
                  accuracy {s.accuracyPct}% · within target {s.withinTargetPct}%
                  {s.badReasonPct != null ? ` · bad reason ${s.badReasonPct}%` : ""}
                  {s.extensions.count ? ` · ${s.extensions.count} extension${s.extensions.count === 1 ? "" : "s"} (${s.extensions.totalSeconds}s)` : ""}
                  {s.timeouts ? ` · ${s.timeouts} timed out` : ""}
                </span>
              )}
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...cell, textAlign: "left" }}>#</th>
                    <th style={{ ...cell, textAlign: "left" }}>Delivery</th>
                    <th style={{ ...cell, textAlign: "left" }}>Decision / key</th>
                    <th style={{ ...cell, textAlign: "left" }}>Elapsed</th>
                    <th style={{ ...cell, textAlign: "left" }} title="&ldquo;+ Time&rdquo; presses on this delivery (§8.4)">+ Time</th>
                    {qids.map((id) => <th key={id} style={{ ...cell, textAlign: "center" }} title={id}>Q{id.match(/^q(\d+)/)?.[1] ?? id}</th>)}
                    <th style={{ ...cell, textAlign: "left" }}>Key</th>
                  </tr>
                </thead>
                <tbody>
                  {a.answers.map((r) => (
                    <tr key={r.id} data-testid={`trainee-answer-${r.id}`}>
                      <td style={cell}>{r.itemIndex + 1}</td>
                      <td style={cell}>{r.recipientName ?? r.shortId ?? r.itemId.slice(0, 8)}</td>
                      <td style={cell}>
                        <span style={{ fontWeight: 700, color: r.decision === "GOOD" ? T.good : r.decision === "BAD" ? T.bad : T.txt3 }}>{r.decision ?? "—"}</span>
                        <span style={{ color: T.txt3 }}> / {r.keyedDecision ?? "—"}</span>
                        {r.timedOut && <span style={{ color: T.amber }}> · timed out</span>}
                      </td>
                      <td style={cell}>{typeof r.elapsedMs === "number" ? `${Math.round(r.elapsedMs / 100) / 10}s` : "—"}</td>
                      <td style={cell} data-testid={`answer-extensions-${r.id}`}>
                        {r.extensions?.count ? `${r.extensions.count} · +${r.extensions.seconds}s` : <span style={{ color: T.txt3 }}>—</span>}
                      </td>
                      {qids.map((id) => <td key={id} style={{ ...cell, textAlign: "center" }} title={r.grades?.[id] ? Object.entries(r.grades[id].fields).map(([f, st]) => `${f}: ${st}`).join("\n") : undefined}>{mark(r.grades?.[id]?.status)}</td>)}
                      <td style={cell}>v{r.keyVersion}{r.regradedAt ? " (regraded)" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Settings tab (Admin) ────────────────────────────────────────────────────

function SettingsTab() {
  const q = useSimulatorSettings(true);
  const update = useUpdateSimulatorSettings();
  const { toast } = useToast();
  const [d, setD] = useState<SimulatorSettings | null>(null);
  useEffect(() => { if (q.data) setD(q.data.settings); }, [q.data]);
  if (q.isLoading || !d) return <div style={{ padding: 12 }}>Loading settings…</div>;
  if (q.isError) return <div style={{ padding: 12, color: T.bad }}>Couldn't load settings (Admin only).</div>;
  const num = (k: keyof SimulatorSettings, label: string, hint: string) => (
    <label style={{ display: "grid", gridTemplateColumns: "220px 100px 1fr", gap: 10, alignItems: "center", fontSize: 13 }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      <input type="number" data-testid={`setting-${k}`} value={Number(d[k])} onChange={(e) => setD({ ...d, [k]: Number(e.target.value) })} style={inputStyle} />
      <span style={{ fontSize: 12, color: T.txt2 }}>{hint}</span>
    </label>
  );
  const save = async () => {
    try {
      await update.mutateAsync(d);
      toast({ title: "Settings saved" });
    } catch (e: any) {
      toast({ title: "Couldn't save settings", description: String(e?.message ?? e), variant: "destructive" });
    }
  };
  return (
    <div data-testid="simulator-settings" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 10, maxWidth: 760 }}>
      {num("targetGoodSeconds", "Target · Good", "pace target when the keyed decision is GOOD (D6)")}
      {num("targetOtherSeconds", "Target · other", "pace target otherwise; also the countdown start")}
      {num("warningSeconds", "Warning at", "chip turns warning (Phase 3–4)")}
      {num("extendSeconds", "+ Time adds", "seconds per press (Phase 3)")}
      {/* §12 Q7 is still open, so these two thresholds drive nothing a Senior sees:
          they feed the stored `passRecommended` only, and the roster offers no
          automatic verdict. Labelled so an Admin does not read one into them. */}
      {num("passAccuracyPct", "Accuracy threshold %", "Stored only — no automatic pass mark (§12 Q7)")}
      {num("passPacePct", "Pace threshold %", "Stored only — no automatic pass mark (§12 Q7)")}
      <label style={{ display: "grid", gridTemplateColumns: "220px 100px 1fr", gap: 10, alignItems: "center", fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>Phase 2 clock enforced</span>
        <Switch checked={d.phase2Enforced} onCheckedChange={(v) => setD({ ...d, phase2Enforced: v })} />
        <span style={{ fontSize: 12, color: T.txt2 }}>if on, the Phase 2 clock behaves like Phase 3</span>
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 10, alignItems: "center", fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>Items per phase</span>
        <div style={{ display: "flex", gap: 10 }}>
          {SIMULATOR_PHASES.map((p) => (
            <label key={p} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12.5 }}>P{p}
              <input type="number" min={1} data-testid={`setting-phaseSize-${p}`} value={d.phaseSizes[p] ?? ""} onChange={(e) => setD({ ...d, phaseSizes: { ...d.phaseSizes, [p]: Number(e.target.value) } })} style={{ ...inputStyle, width: 70 }} />
            </label>
          ))}
          <span style={{ fontSize: 12, color: T.txt2, alignSelf: "center" }}>sum = served-set cap ({Object.values(d.phaseSizes).reduce((a, b) => a + (b || 0), 0)})</span>
        </div>
      </div>
      <div><Button size="sm" data-testid="settings-save" disabled={update.isPending} onClick={save}>{update.isPending ? "Saving…" : "Save settings"}</Button></div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function TrainManage() {
  const { isAdmin, isSenior, role } = useRole();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const tab = useMemo<TabValue>(() => {
    const t = new URLSearchParams(search).get("tab");
    return (TAB_VALUES as readonly string[]).includes(t ?? "") ? (t as TabValue) : "training-set";
  }, [search]);
  if (role === null) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!isAdmin && !isSenior) return <div style={{ padding: 24 }}>Senior or Admin access required.</div>;
  return (
    <div style={{ padding: "14px 22px 40px", fontFamily: Starling.fontBody, color: T.text }} data-testid="train-manage">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <div style={{ fontFamily: Starling.fontHead, fontSize: 18, fontWeight: 600, color: T.purple }}>Training</div>
        <div style={{ fontSize: 12.5, color: T.txt2 }}>Build the training set, key the answers, and write the lessons before any trainee starts.</div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setLocation(`/train/manage?tab=${v}`)}>
        <TabsList data-testid="train-manage-tabs">
          <TabsTrigger value="training-set" data-testid="tab-training-set">Training Set</TabsTrigger>
          <TabsTrigger value="trainees" data-testid="tab-trainees">Trainees</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">Questions</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="tab-lessons">Lessons</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="training-set"><TrainingSetTab /></TabsContent>
        <TabsContent value="trainees"><TraineesTab /></TabsContent>
        <TabsContent value="questions"><QuestionsTab canEdit={isAdmin} /></TabsContent>
        <TabsContent value="lessons"><LessonsTab /></TabsContent>
        {isAdmin && <TabsContent value="settings"><SettingsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
